import { useEffect } from 'react';
import { useQuery } from '@apollo/client';

import logger from 'utils/logger';
import { msUntilNextMidnight } from 'app/utils/date';

import { LibraryTrainingPlans as LIBRARY_TRAINING_PLANS } from 'app/ui/components/molecules/library-training-plans/query.gql';
import type { LibraryTrainingPlansQuery, ProgressivePlan, ProgressivePlanInstance } from 'app/types/graphql';

export type LibraryTrainingPlansState =
  /** Nothing to show yet: the section renders its skeleton. */
  | { status: 'loading' }
  /** The fetch failed with nothing cached (or the feature is off): the section hides itself. */
  | { status: 'unavailable' }
  | { status: 'ready'; planInstances: ProgressivePlanInstance[]; plans: Pick<ProgressivePlan, 'id'>[] };

type UseLibraryTrainingPlansOptions = {
  skip?: boolean;
};

// Refetch this long after midnight, so the API has moved on to the new day's workout too.
const ROLLOVER_GRACE_MS = 1_000;

/**
 * The active training plans behind the plan-card rail on home and the library.
 *
 * Call it from the page, above the page's loading gate, so this request goes out in parallel
 * with the page query instead of after it; the plans were the slowest thing either page asked
 * for, so they are no longer part of the page operation (see query.gql).
 *
 * `cache-and-network` shows whatever a previous visit (or the other page, which runs the same
 * operation) cached, then revalidates so a plan started elsewhere is picked up; `nextFetchPolicy`
 * keeps later re-renders on the cache. Because nothing then re-triggers a fetch for the life of
 * the mount, the day rollover is handled explicitly: each active instance's "today" is computed
 * by the API in the instance's own timezone, so a refetch is scheduled for the next midnight in
 * each of those zones. Home used to get a coarser version of this for free from its `$date`
 * variable changing on re-render; this operation has no date argument to hang that on.
 */
export const useLibraryTrainingPlans = ({
  skip = false,
}: UseLibraryTrainingPlansOptions = {}): LibraryTrainingPlansState => {
  const { data, previousData, loading, error, refetch } = useQuery<LibraryTrainingPlansQuery>(LIBRARY_TRAINING_PLANS, {
    skip,
    fetchPolicy: 'cache-and-network',
    nextFetchPolicy: 'cache-first',
  });

  // Starting a plan evicts allProgressivePlanInstances and allProgressivePlans from the cache, so
  // the watcher briefly reports loading with no data. Keep the last result on screen through that
  // refetch rather than flashing the skeleton (which would also unmount the banner that owns the
  // "plan started" dialog); the same keeps a stale-but-usable section up if a revalidation fails.
  const shown = data ?? previousData;

  useEffect(() => {
    if (error && !shown) {
      logger.error({ message: 'Failed to load the active training plans; hiding the section', error });
    }
  }, [error, shown]);

  const planInstances = (shown?.progressivePlanInstances?.planInstances ?? []) as ProgressivePlanInstance[];
  const plans = (shown?.progressivePlans?.nodes ?? []) as Pick<ProgressivePlan, 'id'>[];

  // One key per distinct set of zones, so the timer is only re-armed when the plans themselves change.
  const timezoneKey = Array.from(new Set(planInstances.map((instance) => instance.timezone)))
    .sort()
    .join('\n');

  useEffect(() => {
    if (!timezoneKey) {
      return;
    }
    const timezones = timezoneKey.split('\n');
    let timer: ReturnType<typeof setTimeout>;

    const armForNextMidnight = () => {
      const delay = Math.min(...timezones.map((timezone) => msUntilNextMidnight(timezone))) + ROLLOVER_GRACE_MS;
      timer = setTimeout(() => {
        refetch();
        armForNextMidnight();
      }, delay);
    };

    armForNextMidnight();
    return () => clearTimeout(timer);
  }, [timezoneKey, refetch]);

  if (!shown) {
    return loading ? { status: 'loading' } : { status: 'unavailable' };
  }

  return { status: 'ready', planInstances, plans };
};
